/**
 * Shared client-deactivation contract.
 *
 * The values are intentionally explicit so a preview can be confirmed without
 * the frontend inferring accounting policy from invoice state or balances.
 */

export const CLIENT_DEACTIVATION_CUTOFF_STRATEGY = {
  IMMEDIATE: 'IMMEDIATE',
  END_OF_PERIOD: 'END_OF_PERIOD',
} as const;

export type ClientDeactivationCutoffStrategy =
  (typeof CLIENT_DEACTIVATION_CUTOFF_STRATEGY)[keyof typeof CLIENT_DEACTIVATION_CUTOFF_STRATEGY];

export const CLIENT_DEACTIVATION_CURRENT_PERIOD_TREATMENT = {
  CHARGE_SERVED_DAYS: 'CHARGE_SERVED_DAYS',
  FULL_PERIOD: 'FULL_PERIOD',
  FORGIVE_CURRENT_PERIOD: 'FORGIVE_CURRENT_PERIOD',
} as const;

export type ClientDeactivationCurrentPeriodTreatment =
  (typeof CLIENT_DEACTIVATION_CURRENT_PERIOD_TREATMENT)[keyof typeof CLIENT_DEACTIVATION_CURRENT_PERIOD_TREATMENT];

export const CLIENT_DEACTIVATION_OUTCOME = {
  SCHEDULED: 'SCHEDULED',
  APPLIED: 'APPLIED',
  IDEMPOTENT: 'IDEMPOTENT',
  BLOCKED: 'BLOCKED',
  REACTIVATED: 'REACTIVATED',
} as const;

export type ClientDeactivationOutcome =
  (typeof CLIENT_DEACTIVATION_OUTCOME)[keyof typeof CLIENT_DEACTIVATION_OUTCOME];

/** Persisted operation states use the existing Spanish domain vocabulary. */
export const CLIENT_DEACTIVATION_OPERATION_STATUS = {
  SCHEDULED: 'PROGRAMADA',
  APPLIED: 'APLICADA',
  BLOCKED: 'BLOQUEADA',
  REACTIVATED: 'REACTIVADA',
} as const;

export type ClientDeactivationOperationStatus =
  (typeof CLIENT_DEACTIVATION_OPERATION_STATUS)[keyof typeof CLIENT_DEACTIVATION_OPERATION_STATUS];

export const CLIENT_DEACTIVATION_INVOICE_STATUS = {
  NONE: 'NONE',
  PAID: 'PAID',
  PARTIAL: 'PARTIAL',
  UNPAID: 'UNPAID',
} as const;

export type ClientDeactivationInvoiceStatus =
  (typeof CLIENT_DEACTIVATION_INVOICE_STATUS)[keyof typeof CLIENT_DEACTIVATION_INVOICE_STATUS];

export const CLIENT_DEACTIVATION_ERROR_CODE = {
  REQUIRED: 'CLIENT_DEACTIVATION_REQUIRED',
  PREVIEW_STALE: 'CLIENT_DEACTIVATION_PREVIEW_STALE',
  OPERATION_KEY_REUSED: 'CLIENT_DEACTIVATION_OPERATION_KEY_REUSED',
  POLICY_INVALID: 'CLIENT_DEACTIVATION_POLICY_INVALID',
  PREPAID_SHAPE_UNRESOLVED: 'CLIENT_DEACTIVATION_PREPAID_SHAPE_UNRESOLVED',
  ALREADY_INACTIVE: 'CLIENT_DEACTIVATION_ALREADY_INACTIVE',
  DUE_UNRESOLVED: 'CLIENT_DEACTIVATION_DUE_UNRESOLVED',
} as const;

export type ClientDeactivationErrorCode =
  (typeof CLIENT_DEACTIVATION_ERROR_CODE)[keyof typeof CLIENT_DEACTIVATION_ERROR_CODE];

export interface ClientDeactivationDayCount {
  totalDays: number;
  servedDays: number;
}

/** A prepaid period that can safely become client credit after deactivation. */
export interface ClientDeactivationPrepaidPeriod {
  packageId: number;
  periodId: number;
  periodStart: string;
  periodEnd: string;
  amount: string;
}

/** Canonical current-period invoice values used by the preview hash. */
export interface ClientDeactivationCurrentPeriod {
  invoiceId: number;
  periodStart: string;
  periodEnd: string;
  dueDate: string | null;
  invoiceStatus: ClientDeactivationInvoiceStatus;
  grossAmount: string;
  adjustmentsApplied: string;
  paymentApplied: string;
  remainder: string;
  totalDays: number;
  servedDays: number;
  estimatedServedAmount: string;
  /** Amount the ledger will append as an adjustment. */
  estimatedAdjustment: string;
  /** Estimated overpayment credit produced by that adjustment. */
  estimatedCredit: string;
}

export interface ClientDeactivationFinancialSnapshot {
  currentPeriod: ClientDeactivationCurrentPeriod | null;
  historicalDebtPreserved: string;
  futurePrepaidCredit: string;
  futurePrepaidPeriods: ClientDeactivationPrepaidPeriod[];
}

export interface ClientDeactivationServiceSnapshot {
  serviceId: number;
  currentState: string;
  targetState: string;
  willChange: boolean;
}

export interface ClientDeactivationPreviewResponse {
  idCliente: number;
  reason: string;
  cutoffStrategy: ClientDeactivationCutoffStrategy;
  currentPeriodTreatment: ClientDeactivationCurrentPeriodTreatment;
  operationKey: string;
  effectiveDate: string;
  previewHash: string;
  financial: ClientDeactivationFinancialSnapshot;
  services: ClientDeactivationServiceSnapshot[];
}

export interface ClientDeactivationResponse {
  operationId: number;
  idCliente: number;
  outcome: ClientDeactivationOutcome;
  status: ClientDeactivationOperationStatus;
  reason: string;
  actor: string;
  cutoffStrategy: ClientDeactivationCutoffStrategy;
  currentPeriodTreatment: ClientDeactivationCurrentPeriodTreatment;
  operationKey: string;
  effectiveDate: string;
  previewHash: string;
  /** Total append-only adjustment registered against the current invoice. */
  currentPeriodAdjustment: string;
  /** Credit generated when an applied payment exceeds served current value. */
  currentPeriodCredit: string;
  futurePrepaidCredit: string;
  historicalDebtPreserved: string;
  affectedServiceIds: number[];
  appliedAt: string | null;
  blockedErrorCode: ClientDeactivationErrorCode | null;
  blockedMessage: string | null;
}

/**
 * Calendar arithmetic for periods represented as [start, end).
 * Date-only values are parsed in UTC so the result is independent of the host
 * process timezone.
 */
export function calculateExclusiveDayCount(startDate: string, endDate: string): number {
  const start = parseDateOnly(startDate);
  const end = parseDateOnly(endDate);
  if (!Number.isFinite(start) || !Number.isFinite(end)) {
    throw new Error('Client deactivation dates must be valid YYYY-MM-DD values');
  }
  return Math.max(0, Math.round((end - start) / 86_400_000));
}

function parseDateOnly(value: string): number {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return Number.NaN;
  const timestamp = Date.parse(`${value}T00:00:00Z`);
  if (!Number.isFinite(timestamp)) return Number.NaN;
  return new Date(timestamp).toISOString().slice(0, 10) === value ? timestamp : Number.NaN;
}

export function calculateClientDeactivationDays(
  periodStart: string,
  periodEnd: string,
  effectiveDate: string,
): ClientDeactivationDayCount {
  const totalDays = calculateExclusiveDayCount(periodStart, periodEnd);
  const servedBeforeCutoff = calculateExclusiveDayCount(periodStart, effectiveDate);
  return {
    totalDays,
    servedDays: Math.min(totalDays, servedBeforeCutoff),
  };
}
